import {
  type PageAdminEvaluationsData,
  QueryReference,
} from '@agentselly/immodossier-shared';
import { useNavigate } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { formatDateTime, WidgetTitle, WidgetWrapper } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import ReactJson from 'react-json-view';
import { type ReactNode, useState } from 'react';

export function LegacyEvaluationsPage(props: {
  readonly search: Record<string, string | string[] | undefined>;
  readonly fullPath: string;
}) {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const currentFilter: QueryFilter = props.search;

  const query = useBackendQuery(QueryReference.PageAdminEvaluations);

  const [
    dialogContent,
    setDialogContent,
  ] = useState('');

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  async function updateFilter(filter: QueryFilter) {
    const nextSearch: QueryFilter = {};

    for (const key of [
      'page',
      'showFollowup',
      'showInternal',
      'hideErrors',
      'hideSuccess',
      'wannaSell',
      'appId',
      'showOnlyValiant',
    ]) {
      const value = filter[key];

      if (typeof value === 'undefined') {
        continue;
      }

      if (!value.length) {
        // Skip empty arrays and strings
        continue;
      }

      nextSearch[key] = value;
    }

    await navigate({
      to: props.fullPath,
      search: nextSearch as never,
      replace: true,
    } as never);

    await query.refetch();
  }

  const paginationProps: PaginationWidgetProps = {
    pagination: query.data?.pagination,
    resultDate: formatDateTime(query.data?.resultTimestamp),
    updateFilter: updateFilter,
  };

  return (
    <div className="flex flex-col gap-8">
      {dialogContent ? (
        <div className="fixed top-0 right-0 bottom-0 left-0 z-[999999] flex items-center justify-center text-white">
          <div
            className="absolute top-0 right-0 bottom-0 left-0 bg-black opacity-80"
            onClick={() => setDialogContent('')}
          ></div>
          <div
            className="relative z-1 whitespace-pre font-mono p-8 rounded-lg bg-white dark:bg-gray-800 text-black dark:text-gray-100 dark:text-white dark:text-white min-w-[50svw] max-w-[90svw] min-h-[50svh] max-h-[90svh] overflow-auto"
            onClick={() => false}
          >
            <ReactJson src={JSON.parse(dialogContent)} />
          </div>
        </div>
      ) : null}
      <WidgetWrapper>
        <WidgetTitle>{t('admin_evaluations_filter_title')}</WidgetTitle>
        <br />
        {/*<div>*/}
        {/*  <label htmlFor="showFollowup">*/}
        {/*    <input*/}
        {/*      id="showFollowup"*/}
        {/*      type="checkbox"*/}
        {/*      onChange={(event) =>*/}
        {/*        updateFilter({*/}
        {/*          showFollowup: event.target.checked ? 'true' : '',*/}
        {/*        })*/}
        {/*      }*/}
        {/*      defaultChecked={Boolean(currentFilter.showFollowup)}*/}
        {/*    />*/}
        {/*    &nbsp;*/}
        {/*    <span className="border-b-2 border-blue-600">*/}
        {/*      FOLLOWUP-Bewertungen anzeigen*/}
        {/*    </span>*/}
        {/*  </label>*/}
        {/*</div>*/}
        <div>
          <label htmlFor="showInternal">
            <input
              id="showInternal"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  showInternal: event.target.checked ? 'true' : '',
                })
              }
              defaultChecked={Boolean(currentFilter.showInternal)}
            />
            &nbsp;
            <span className="border-b-2 border-blue-600">
              Interne Bewertungen anzeigen
            </span>
          </label>
        </div>
        <div>
          <label htmlFor="hideErrors">
            <input
              id="hideErrors"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  hideErrors: event.target.checked ? '' : 'true',
                })
              }
              defaultChecked={!currentFilter.hideErrors}
            />
            &nbsp;
            <span className="border-b-2 border-red-600">
              Fehler Bewertungen anzeigen
            </span>
          </label>
        </div>
        <div>
          <label htmlFor="hideSuccess">
            <input
              id="hideSuccess"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  hideSuccess: event.target.checked ? '' : 'true',
                })
              }
              defaultChecked={!currentFilter.hideSuccess}
            />
            &nbsp;
            <span className="border-b-2 border-green-600">
              Erfolgreiche Bewertungen anzeigen
            </span>
          </label>
        </div>
        <div className="flex flex-row gap-4 mt-8">
          <div>
            <label htmlFor="wannaSell">
              Bewertungsgrund:&nbsp;
              <select
                id="wannaSell"
                onChange={(event) =>
                  updateFilter({
                    wannaSell: event.target.value,
                  })
                }
                defaultValue={currentFilter.wannaSell || ''}
              >
                <option value="">Alle</option>
                <option value="sell12">SQL (sell12)</option>
                <option value="sell24">MQL (sell24)</option>
                <option value="sell">Unklar (sell)</option>
                <option value="buy">Buy</option>
                <option value="value">Value</option>
              </select>
            </label>
          </div>
          <div>
            <label htmlFor="appId">
              AppId:&nbsp;
              <select
                id="appId"
                onChange={(event) =>
                  updateFilter({
                    appId: event.target.value,
                  })
                }
                defaultValue={currentFilter.appId || ''}
              >
                <option value="">Alle</option>
                <option value="en6fcr">en6fcr</option>
                <option value="dca7b5">dca7b5</option>
              </select>
            </label>
          </div>
        </div>
        <div>
          <label htmlFor="showOnlyValiant">
            <input
              id="showOnlyValiant"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  showOnlyValiant: event.target.checked ? 'true' : '',
                })
              }
              defaultChecked={Boolean(currentFilter.showOnlyValiant)}
            />
            &nbsp;
            <span className="border-b-2 border-violet-600">
              Nur Valiant Bewertungen anzeigen
            </span>
          </label>
        </div>
      </WidgetWrapper>
      <PaginationWidget {...paginationProps} />
      <WidgetWrapper>
        <WidgetTitle>{t('admin_evaluations_results_title')}</WidgetTitle>
        <br />
        <div className="flex flex-col gap-4">
          {query.data?.evaluations
            ?.filter(Boolean)
            ?.map((evaluation, index) => {
              return (
                <div
                  key={index}
                  className={clsx('border-2 p-4 pl-8 rounded-md relative', {
                    'border-green-600 bg-green-50':
                      evaluation.isSuccessful && !evaluation.hasError,
                    'border-red-600 bg-red-50':
                      evaluation.hasError && !evaluation.isSuccessful,
                    // Yellow as fallback
                    'border-yellow-600 bg-yellow-50':
                      (evaluation.hasError && evaluation.isSuccessful) ||
                      (!evaluation.isSuccessful && !evaluation.hasError),
                  })}
                >
                  <div
                    className={clsx(
                      'absolute w-1 top-2 bottom-2 left-2 rounded',
                      {
                        'bg-violet-600': evaluation.valiantDeviceId,
                        'bg-blue-600': evaluation.isInternal,
                        'bg-cyan-600':
                          evaluation.isFollowup && !evaluation.isInternal,
                        'bg-gray-300':
                          !evaluation.isFollowup && !evaluation.isInternal,
                      },
                    )}
                  ></div>
                  <div className="flex flex-col gap-2 items-stretch">
                    <p className="flex flex-row gap-2 items-center">
                      <span>{formatDateTime(evaluation.timestamp)}</span>
                      <strong>{evaluation.address}</strong>
                      <small>{evaluation.sessionId}</small>
                    </p>
                    <p className="flex flex-row gap-2 items-center text-xs">
                      {[
                        evaluation.contactName,
                        evaluation.contactEmail,
                        evaluation.contactPhone,
                      ]
                        .filter(Boolean)
                        .map((key, index) => (
                          <span key={index}>{key}</span>
                        ))}
                    </p>
                    <p className="flex flex-row gap-2 items-center text-xs">
                      {Object.entries({
                        'IAZI-Bewertung': evaluation.iaziEvaluation,
                        TrustScore: evaluation.trustScore,
                        Bewertungsgrund: evaluation.wannaSell,
                        AppId: evaluation.appId,
                        CalculatorVersion:
                          evaluation.rawCalculatorResult.calculatorVersion,
                        'UTM Source': evaluation.utmSource,
                        'UTM Medium': evaluation.utmMedium,
                        'UTM Campaign': evaluation.utmCampaign,
                      })
                        .filter((it) => Boolean(it[1]))
                        .map(
                          ([
                            key,
                            value,
                          ]) => (
                            <span key={key}>
                              {key}: {value}
                            </span>
                          ),
                        )}
                    </p>
                    <p className="flex flex-row gap-2 items-center text-xs">
                      {evaluation.valiantDeviceId ? '🟪' : ''}
                      {Object.entries({
                        'Valiant Device ID': evaluation.valiantDeviceId,
                        'Valiant Banner Accepted At':
                          evaluation.valiantBannerAcceptedAt,
                      })
                        .filter((it) => Boolean(it[1]))
                        .map(
                          ([
                            key,
                            value,
                          ]) => (
                            <span key={key}>
                              {key}: {value}
                            </span>
                          ),
                        )}
                    </p>
                    <p className="flex flex-row flex-wrap gap-2 items-center text-xs">
                      {Object.entries({
                        'Resultat-Seite': evaluation.links.resultpage,
                        'HubSpot Property': evaluation.links.hubspotProperty,
                        'HubSpot Contact': evaluation.links.hubspotContact,
                        'Admin-Input': evaluation.links.adminInput,
                        'Admin-Result': evaluation.links.adminResult,
                      })
                        .filter((it) => Boolean(it[1]))
                        .map(
                          ([
                            key,
                            value,
                          ]) => (
                            <ExternalLink
                              key={key}
                              href={value}
                            >
                              {key}
                            </ExternalLink>
                          ),
                        )}
                      <span className="flex-1"></span>
                      {Object.entries({
                        'UTM anzeigen': evaluation.rawUTM,
                        'CalculatorInput anzeigen':
                          evaluation.rawCalculatorInput,
                        'CalculatorResult anzeigen':
                          evaluation.rawCalculatorResult,
                        'Dossier anzeigen': evaluation.rawDossier,
                      })
                        .filter((it) => Boolean(it[1]))
                        .map(
                          ([
                            key,
                            value,
                          ]) => (
                            <button
                              key={key}
                              className={clsx(
                                'disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-600 dark:disabled:text-gray-400 dark:text-gray-400 dark:disabled:text-gray-400 text-white px-1 rounded float-end',
                                {
                                  'bg-green-600 hover:bg-green-800':
                                    evaluation.isSuccessful &&
                                    !evaluation.hasError,
                                  'bg-red-600 hover:bg-red-800':
                                    evaluation.hasError &&
                                    !evaluation.isSuccessful,
                                  // Yellow as fallback
                                  'bg-yellow-600 hover:bg-yellow-800':
                                    (evaluation.hasError &&
                                      evaluation.isSuccessful) ||
                                    (!evaluation.isSuccessful &&
                                      !evaluation.hasError),
                                },
                              )}
                              onClick={() =>
                                setDialogContent(JSON.stringify(value))
                              }
                              disabled={!value}
                            >
                              {key}
                            </button>
                          ),
                        )}
                    </p>
                    {evaluation.errorType ? (
                      <div className="bg-red-300 rounded px-2 py-1">
                        <p>
                          LatestErrorType: {evaluation.errorType}
                          {evaluation.errorData ? (
                            <>
                              {' '}
                              ({evaluation.errorData.source}:{' '}
                              {evaluation.errorData.message})
                            </>
                          ) : null}
                        </p>
                        {evaluation.errorData?.stack ? (
                          <details>
                            <summary className="cursor-pointer">stack</summary>
                            <pre className="whitespace-pre-wrap text-xs">
                              {evaluation.errorData.stack}
                            </pre>
                          </details>
                        ) : null}
                      </div>
                    ) : null}
                    {evaluation.rawCalculatorResult?.legacyImportLabel ? (
                      <p className="bg-yellow-300 rounded px-2">
                        LegacyImportLabel:{' '}
                        {evaluation.rawCalculatorResult.legacyImportLabel}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
        </div>
      </WidgetWrapper>
      <PaginationWidget {...paginationProps} />
      <WidgetWrapper>
        <WidgetTitle>{t('admin_evaluations_legend_title')}</WidgetTitle>
        <br />
        {[
          [
            'text-green-200',
            'Erfolg',
          ],
          [
            'text-violet-200',
            'Valiant Bewertung',
          ],
          [
            'text-blue-200',
            'Interne Bewertung',
          ],
          [
            'text-cyan-200',
            'Followup Bewertung',
          ],
          [
            'text-yellow-200',
            'Warnung (Zugriff auf Resultat-Seite)',
          ],
          [
            'text-red-200',
            'Fehler (Kein Zugriff auf die Resultat-Seite)',
          ],
        ].map(
          (
            [
              color,
              label,
            ],
            index,
          ) => (
            <p key={index}>
              <span className={color}>█</span>: {label}
            </p>
          ),
        )}
      </WidgetWrapper>
    </div>
  );
}

interface QueryFilter extends Record<string, string | string[] | undefined> {
  readonly page?: string;
  readonly showFollowup?: string;
  readonly showInternal?: string;
  readonly hideErrors?: string;
  readonly hideSuccess?: string;
  readonly wannaSell?: string;
  readonly appId?: string;
  readonly showOnlyValiant?: string;
}

function PaginationWidget(props: PaginationWidgetProps) {
  const { t } = useTranslate();

  const page = props.pagination?.page || 0;
  const pageCount = props.pagination?.pageCount || 0;

  return (
    <WidgetWrapper>
      <div className="flex flex-col lg:flex-row gap-8 lg:justify-between">
        <PaginationButton
          disabled={page === 1}
          updateFilter={props.updateFilter}
          targetPage={1}
          text={t('admin_evaluations_pagination_first')}
        />
        <PaginationButton
          disabled={page - 1 < 1}
          updateFilter={props.updateFilter}
          targetPage={page - 1}
          text={t('admin_evaluations_pagination_previous')}
        />
        <p className="text-center">
          {t('admin_evaluations_pagination_text', {
            page: page,
            total: pageCount,
          })}
          <br />
          <small>
            {t('admin_evaluations_pagination_results', {
              resultCount: props.pagination?.total || 0,
              resultDate: props.resultDate,
            })}
          </small>
        </p>
        <PaginationButton
          disabled={page + 1 > pageCount}
          updateFilter={props.updateFilter}
          targetPage={page + 1}
          text={t('admin_evaluations_pagination_next')}
        />
        <PaginationButton
          disabled={page === pageCount}
          updateFilter={props.updateFilter}
          targetPage={pageCount}
          text={t('admin_evaluations_pagination_last')}
        />
      </div>
    </WidgetWrapper>
  );
}

interface PaginationWidgetProps {
  readonly pagination: PageAdminEvaluationsData['pagination'] | undefined;
  readonly resultDate: string;
  readonly updateFilter: (filter: QueryFilter) => void;
}

function PaginationButton(props: PaginationButtonProps) {
  return (
    <button
      onClick={() =>
        props.updateFilter({
          page: props.targetPage?.toString() || undefined,
        })
      }
      disabled={props.disabled}
      className={clsx(
        'px-4 py-2 rounded-md',
        props.disabled
          ? 'bg-gray-200 dark:bg-gray-700'
          : 'bg-secondary-600 text-white',
      )}
    >
      {props.text}
    </button>
  );
}

interface PaginationButtonProps {
  readonly disabled: boolean;
  readonly updateFilter: (filter: QueryFilter) => void;
  readonly targetPage: number;
  readonly text: string;
}

function ExternalLink(props: ExternalLinkProps) {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noreferrer"
      className="text-primary-600 underline"
    >
      {props.children}
    </a>
  );
}

interface ExternalLinkProps {
  readonly href: string;
  readonly children: ReactNode;
}
